import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { DynamicResultTable, FormattedAddress } from '@css-xp/base';
import { INVOICE_GROUP_PATH_2 } from '@atlassian/cssxp-typescommon';
import { fetchBillingProfilesByPaymentMethodIdQuery } from '../queries/fetch-billing-profile-by-payment-method-id';
import './remove-payment-method-sidebar.scss';

export const emptyStateBillingProfileProp = {
  header: '',
  description: 'No Billing profiles to show',
  imageUrl: '',
  testId: 'no-results-billingProfile-state',
};

const columns = {
  cells: [
    {
      content: 'Billing profile name',
      width: 15,
    },
    {
      content: 'Sold-to address',
      width: 25,
    },
    {
      content: 'Sites',
      width: 10,
    },
    {
      content: 'Subscriptions',
      width: 10,
    },
  ],
};

interface Props {
  paymentMethodId?: string;
}

const AssociatedBillingProfilesTable = ({ paymentMethodId }: Props) => {
  const { data, loading } = useQuery(fetchBillingProfilesByPaymentMethodIdQuery, {
    variables: {
      id: paymentMethodId,
    },
  });

  const billingProfiles = data?.billingProfilesByPaymentMethodId?.billingProfiles || [];

  const rows = billingProfiles.map((billingProfileItem: any) => {
    return {
      key: billingProfileItem?.id,
      cells: [
        {
          content: <Link to={`${INVOICE_GROUP_PATH_2}/${billingProfileItem?.id}`}>{billingProfileItem?.name}</Link>,
        },
        {
          content: (
            <FormattedAddress
              addressLine1={billingProfileItem?.shipTo?.address1}
              addressLine2={billingProfileItem?.shipTo?.address2}
              city={billingProfileItem?.shipTo?.city}
              country={billingProfileItem?.shipTo?.country}
              postcode={billingProfileItem?.shipTo?.postalCode}
              state={billingProfileItem?.shipTo?.state}
              organizationName={billingProfileItem?.shipTo?.name}
            />
          ),
        },
        {
          content: billingProfileItem?.numberOfCloudsites,
        },
        {
          content: billingProfileItem?.numberOfSubscriptions,
        },
      ],
    };
  });

  const getSubheading = () => {
    if (loading) return null;
    if (billingProfiles?.length) {
      return 'This payment method will also be removed from the following billing profiles';
    }
    return 'There are no associated billing profiles linked to this payment method.';
  };

  return (
    <div className="associated-billing-profiles">
      <div className="heading">Associated billing profiles</div>
      <div className="subheading">{getSubheading()}</div>
      {(!!billingProfiles?.length || loading) && (
        <DynamicResultTable
          testId={'associated-billing-profiles-table'}
          emptyStateProps={emptyStateBillingProfileProp}
          head={columns}
          rows={rows}
          isLoading={loading}
          isSearchEnabled={false}
          isPaginationEnabled={false}
          boxShadow
          className="associated-billing-profiles-table"
        />
      )}
    </div>
  );
};

export default AssociatedBillingProfilesTable;
